import { FC, useState } from 'react'

import { keys } from 'lodash-es'

import {
  PickupDeliveryLocationDeleteReason,
  PickupDeliveryLocationResponseV1,
} from '@forto-dev/customer-master-data-models'
import {
  Alert,
  Button,
  Cluster,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LabelValue,
  Select,
  Separator,
  Spinner,
  Stack,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { usePickupDeliveryLocationDeleteMutation } from '../../api/pickupDeliveryLocation.service'

type LocationDeleteDialogProps = {
  location: PickupDeliveryLocationResponseV1
  onClose: (isDeleted?: boolean) => void
}

export const LocationDeleteDialog: FC<LocationDeleteDialogProps> = ({ location, onClose }) => {
  const toaster = useToaster()
  const { translate } = useLocalisation()
  const [deleteReason, setDeleteReason] = useState<PickupDeliveryLocationDeleteReason | undefined>()

  const { mutate: deleteLocation, isPending } = usePickupDeliveryLocationDeleteMutation()

  const handleDeleteLocation = async () => {
    if (!deleteReason) {
      return
    }
    const request = {
      id: location.id,
      request: {
        deleteReason,
      },
      accountID: location.accountID,
    }

    deleteLocation(request, {
      onSuccess: () => {
        toaster({
          variant: 'positive',
          title: translate('locations.delete.successMessage'),
        })
        onClose(true)
      },
      onError: (error) => {
        toaster({
          variant: 'negative',
          title: translate('locations.delete.errorMessage'),
          description: error instanceof Error ? error.message : 'An error occurred. Please try again later.',
        })
      },
    })
  }

  return (
    <Dialog
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose()
        }
      }}
    >
      <DialogContent maxWidth="screen-xs">
        <DialogHeader>
          <DialogTitle>{translate('locations.delete.dialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={16}>
            <Typography>{translate('locations.delete.dialog.confirmationMessage')}</Typography>
            <Separator orientation="horizontal" />
            <LabelValue label={translate('locations.delete.dialog.locationName')} value={location.locationName} />
            <LabelValue label={translate('locations.delete.dialog.address')} value={location.addressFormatted} />
            <Separator orientation="horizontal" />
            <Select
              id="delete-location-reason"
              required
              label={translate('locations.delete.dialog.reason.inputLabel')}
              placeholder={translate('locations.delete.dialog.reason.inputPlaceholder')}
              value={deleteReason}
              items={keys(PickupDeliveryLocationDeleteReason).map((key) => {
                const reasonKey = key as keyof typeof PickupDeliveryLocationDeleteReason
                return {
                  label: translate(`locations.delete.dialog.reason.${reasonKey}`),
                  value: PickupDeliveryLocationDeleteReason[reasonKey],
                }
              })}
              onChange={(value) => setDeleteReason(value as PickupDeliveryLocationDeleteReason)}
            />
            <Alert variant="warning" title={translate('locations.delete.dialog.alertMessage')} />
          </Stack>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{translate('actions.cancel')}</Button>
          </DialogClose>
          <Button variant="negative" disabled={!deleteReason} onClick={handleDeleteLocation}>
            <Cluster gap={8}>
              {isPending && <Spinner size="xs" />}
              {translate('actions.delete')}
            </Cluster>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
